import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { hrLeaveCreditsApi, hrEmployeesApi, type LeaveCreditDto, type EmployeeDto } from '../../services/api';

export type LeaveCreditType = 'trainee' | 'regular' | 'supervisor' | 'manager';

export interface LeaveCredit {
  id: string;
  name: string;
  days: number;
  type: LeaveCreditType;
}

const TYPE_OPTIONS: { value: LeaveCreditType; label: string }[] = [
  { value: 'trainee', label: 'Trainee' },
  { value: 'regular', label: 'Regular' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'manager', label: 'Manager' },
];

function mapDto(dto: LeaveCreditDto): LeaveCredit {
  return {
    id: dto.id,
    type: dto.type,
    name: dto.credit_name,
    days: dto.days,
  };
}

type SettingsTab = 'leave-credits' | 'assign-employee';

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'leave-credits', label: 'Leave Credits' },
  { id: 'assign-employee', label: 'Assign Employee' },
];

function getInitials(emp: { first_name?: string; last_name?: string; full_name?: string }): string {
  if (emp.first_name && emp.last_name) {
    return `${emp.first_name[0]}${emp.last_name[0]}`.toUpperCase();
  }
  const name = emp.full_name?.trim() || '';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase() || '?';
}

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

function avatarColor(id: string): string {
  let n = 0;
  for (let i = 0; i < id.length; i++) n += id.charCodeAt(i);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}

type ReportingNode = { employee: EmployeeDto; reports: ReportingNode[] };

function ReportingNodeItem({
  node,
  avatarColor: getColor,
  getInitials,
}: {
  node: ReportingNode;
  avatarColor: (id: string) => string;
  getInitials: (emp: EmployeeDto) => string;
}) {
  const hasReports = node.reports.length > 0;
  return (
    <li className="list-none">
      <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-50/80">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${getColor(node.employee.id)}`}>
            {getInitials(node.employee)}
          </div>
          <span className="font-medium text-gray-900 truncate">{node.employee.full_name}</span>
          <span className="text-gray-500 text-sm truncate">{node.employee.job_title || ''}</span>
        </div>
        {hasReports && (
          <span className="text-xs font-medium text-violet-600 bg-violet-50 px-2 py-1 rounded">
            {node.reports.length} Direct Report{node.reports.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      {hasReports && (
        <ul className="ml-6 mt-1 space-y-1 border-l border-gray-200 pl-4">
          {node.reports.map((child) => (
            <li key={child.employee.id} className="flex items-center gap-2 py-1">
              <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${getColor(child.employee.id)}`}>
                {getInitials(child.employee)}
              </div>
              <span className="font-medium text-gray-900">{child.employee.full_name}</span>
              <span className="text-gray-500 text-sm">{child.employee.job_title || ''}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function LeaveCreditSettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('leave-credits');
  const [credits, setCredits] = useState<LeaveCredit[]>([]);
  const [type, setType] = useState<LeaveCreditType>('regular');
  const [name, setName] = useState('');
  const [days, setDays] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editType, setEditType] = useState<LeaveCreditType>('regular');
  const [editName, setEditName] = useState('');
  const [editDays, setEditDays] = useState('');

  // Assign Employee tab state
  const [employees, setEmployees] = useState<EmployeeDto[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [employeeSupervisorMap, setEmployeeSupervisorMap] = useState<Record<string, string>>({});
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [assigningEmployeeId, setAssigningEmployeeId] = useState<string | null>(null);

  const load = useCallback(() => {
    hrLeaveCreditsApi
      .list()
      .then((res) => {
        if (res.data) {
          setCredits(res.data.map(mapDto));
        } else {
          setCredits([]);
        }
      })
      .catch(() => {
        setCredits([]);
      });
  }, []);

  const loadEmployees = useCallback(() => {
    setEmployeesLoading(true);
    setEmployeesError(null);
    hrEmployeesApi
      .list()
      .then((res) => {
        if (res.data) {
          setEmployees(res.data);
        } else {
          setEmployees([]);
        }
      })
      .catch(() => {
        setEmployees([]);
        setEmployeesError('Unable to load employees right now.');
      })
      .finally(() => {
        setEmployeesLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Lazy-load employees only when Assign Employee tab becomes active
  useEffect(() => {
    if (activeTab !== 'assign-employee') return;
    if (employees.length === 0 && !employeesLoading) {
      loadEmployees();
    }
  }, [activeTab, employees.length, employeesLoading, loadEmployees]);

  const supervisors = useMemo(
    () =>
      employees.filter((emp) => {
        const type = emp.employee_type?.toLowerCase() ?? '';
        const title = emp.job_title?.toLowerCase() ?? '';
        return type === 'supervisor' || title.includes('supervisor');
      }),
    [employees],
  );

  const regularEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const type = emp.employee_type?.toLowerCase() ?? '';
      const title = emp.job_title?.toLowerCase() ?? '';
      return type === 'regular' || (!title.includes('supervisor') && type !== 'supervisor' && type !== 'manager');
    });
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    if (!employeeSearch.trim()) return employees;
    const q = employeeSearch.trim().toLowerCase();
    return employees.filter(
      (e) =>
        e.full_name?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.department?.toLowerCase().includes(q) ||
        e.job_title?.toLowerCase().includes(q),
    );
  }, [employees, employeeSearch]);

  const assignedCount = useMemo(
    () => employees.filter((e) => employeeSupervisorMap[e.id]).length,
    [employees, employeeSupervisorMap],
  );

  const reportingTree = useMemo((): ReportingNode[] => {
    const roots: ReportingNode[] = [];
    employees.forEach((emp) => {
      const supervisorId = employeeSupervisorMap[emp.id];
      if (!supervisorId) {
        roots.push({ employee: emp, reports: [] });
      }
    });
    function addReports(node: ReportingNode): void {
      employees.forEach((emp) => {
        if (employeeSupervisorMap[emp.id] === node.employee.id) {
          const child: ReportingNode = { employee: emp, reports: [] };
          addReports(child);
          node.reports.push(child);
        }
      });
    }
    roots.forEach(addReports);
    return roots.sort((a, b) => (a.employee.full_name || '').localeCompare(b.employee.full_name || ''));
  }, [employees, employeeSupervisorMap]);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    const numDays = parseInt(days, 10);
    if (!trimmed || isNaN(numDays) || numDays < 0) return;
    hrLeaveCreditsApi
      .create({ type, credit_name: trimmed, days: numDays })
      .then((res) => {
        if (res.data) {
          setCredits((prev) => [...prev, mapDto(res.data!)]);
          setName('');
          setDays('');
        }
      })
      .catch(() => {
        // ignore; could add toast later
      });
  };

  const handleEdit = (id: string) => {
    const item = credits.find((c) => c.id === id);
    if (!item) return;
    setEditingId(id);
    setEditType(item.type);
    setEditName(item.name);
    setEditDays(String(item.days));
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    const trimmed = editName.trim();
    const numDays = parseInt(editDays, 10);
    if (!trimmed || isNaN(numDays) || numDays < 0) return;
    hrLeaveCreditsApi
      .update(editingId, { type: editType, credit_name: trimmed, days: numDays })
      .then((res) => {
        if (res.data) {
          const updated = mapDto(res.data);
          setCredits((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
        }
      })
      .finally(() => {
        setEditingId(null);
        setEditName('');
        setEditDays('');
      });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDays('');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Remove this leave credit type?')) {
      hrLeaveCreditsApi
        .delete(id)
        .then(() => {
          setCredits((prev) => prev.filter((c) => c.id !== id));
          if (editingId === id) handleCancelEdit();
        })
        .catch(() => {
          // ignore; could show error later
        });
    }
  };

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <h1 className="text-lg font-bold text-gray-900 mb-1 shrink-0">
        HR & Payroll · Settings
      </h1>
      <p className="text-sm text-gray-500 mb-4 shrink-0">
        Configure HR and payroll options.
      </p>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4 shrink-0">
        <nav className="flex gap-6" aria-label="Settings tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-white rounded-xl shadow-card border border-gray-100 flex-1 min-h-0 flex flex-col overflow-hidden">
        {activeTab === 'leave-credits' && (
          <>
            <p className="px-4 pt-4 pb-1 text-sm text-gray-500 border-b border-gray-100">
              Leave credit types and days per year. Add as many as you need; each credit can have its own number of days.
            </p>
            {/* Add form */}
        <form
          onSubmit={handleAdd}
          className="p-4 border-b border-gray-100 flex flex-wrap items-end gap-3 shrink-0"
        >
          <div className="w-36">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as LeaveCreditType)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 outline-none bg-white"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Credit name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sick Leave, Vacation"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 outline-none"
            />
          </div>
          <div className="w-24">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Days
            </label>
            <input
              type="number"
              min={0}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 outline-none"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            Add credit
          </button>
        </form>

        {/* List */}
        <div className="flex-1 min-h-0 overflow-auto">
          {credits.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
              <svg
                className="w-12 h-12 text-gray-300 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="font-medium text-gray-600">No leave credits yet</p>
              <p className="text-sm mt-1">Add a credit type and days above.</p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                    Type
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Credit
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                    Days
                  </th>
                  <th className="px-4 py-3 w-28" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {credits.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50/50">
                    {editingId === c.id ? (
                      <>
                        <td className="px-4 py-2">
                          <select
                            value={editType}
                            onChange={(e) => setEditType(e.target.value as LeaveCreditType)}
                            className="w-full px-3 py-1.5 rounded border border-gray-200 text-sm focus:border-brand-500 outline-none bg-white"
                          >
                            {TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full px-3 py-1.5 rounded border border-gray-200 text-sm focus:border-brand-500 outline-none"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            min={0}
                            value={editDays}
                            onChange={(e) => setEditDays(e.target.value)}
                            className="w-full px-3 py-1.5 rounded border border-gray-200 text-sm focus:border-brand-500 outline-none"
                          />
                        </td>
                        <td className="px-4 py-2 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-gray-600">
                          {TYPE_OPTIONS.find((o) => o.value === c.type)?.label ?? c.type}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {c.name}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{c.days} days</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => handleEdit(c.id)}
                            className="text-sm text-brand-600 hover:text-brand-700 font-medium mr-2"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(c.id)}
                            className="text-sm text-red-600 hover:text-red-700 font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
          </>
        )}

        {activeTab === 'assign-employee' && (
          <div className="flex-1 min-h-0 flex flex-col overflow-auto">
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 border-b border-gray-100 shrink-0">
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-gray-900">{employees.length}</p>
                  <p className="text-sm text-gray-500">Total Employees</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-gray-900">{supervisors.length}</p>
                  <p className="text-sm text-gray-500">Supervisors</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-gray-900">{assignedCount}</p>
                  <p className="text-sm text-gray-500">Assigned to Supervisor</p>
                </div>
              </div>
            </div>

            {employeesLoading ? (
              <div className="flex items-center justify-center py-10 text-sm text-gray-500">Loading employees…</div>
            ) : employeesError ? (
              <div className="flex items-center justify-center py-10 text-sm text-red-600">{employeesError}</div>
            ) : (
              <>
                {/* Supervisor Assignment */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h2 className="text-base font-semibold text-gray-900">Supervisor Assignment</h2>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Assign supervisors to employees for task monitoring and approval requests.
                  </p>
                  <div className="relative max-w-md">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">Q</span>
                    <input
                      type="search"
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                      placeholder="Search employees..."
                      className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm placeholder-gray-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 outline-none"
                    />
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-auto">
                  {filteredEmployees.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
                      <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="font-medium text-gray-600">No employees found.</p>
                      <p className="text-sm mt-1">Add employees or adjust your search.</p>
                    </div>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Employee</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Department</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Job Title</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Supervisor</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredEmployees.map((emp) => {
                          const supervisorId = employeeSupervisorMap[emp.id];
                          const supervisor = supervisorId ? employees.find((e) => e.id === supervisorId) : null;
                          const isAssigning = assigningEmployeeId === emp.id;
                          return (
                            <tr key={emp.id} className="hover:bg-gray-50/60">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${avatarColor(emp.id)}`}>
                                    {getInitials(emp)}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{emp.full_name}</p>
                                    <p className="text-xs text-gray-500">{emp.email || '—'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600">{emp.department || '—'}</td>
                              <td className="px-4 py-3 text-gray-600">{emp.job_title || '—'}</td>
                              <td className="px-4 py-3">
                                {isAssigning ? (
                                  <select
                                    autoFocus
                                    value={supervisorId ?? ''}
                                    onChange={(e) => {
                                      setEmployeeSupervisorMap((prev) => ({ ...prev, [emp.id]: e.target.value }));
                                      setAssigningEmployeeId(null);
                                    }}
                                    onBlur={() => setAssigningEmployeeId(null)}
                                    className="max-w-xs px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 outline-none bg-white"
                                  >
                                    <option value="">Not Assigned</option>
                                    {supervisors.map((sup) => (
                                      <option key={sup.id} value={sup.id}>{sup.full_name}</option>
                                    ))}
                                  </select>
                                ) : supervisor ? (
                                  <div className="flex items-center gap-2 text-emerald-600">
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${avatarColor(supervisor.id)}`}>
                                      {getInitials(supervisor)}
                                    </div>
                                    <span className="font-medium">{supervisor.full_name}</span>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">Not Assigned</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => setAssigningEmployeeId(isAssigning ? null : emp.id)}
                                  className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-600 hover:text-violet-700"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                  Assign
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Reporting Structure */}
                <div className="p-4 border-t border-gray-100 mt-4">
                  <h2 className="text-base font-semibold text-gray-900 mb-1">Reporting Structure</h2>
                  <p className="text-sm text-gray-500 mb-4">Overview of supervisor employee relationships.</p>
                  {reportingTree.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4">No reporting structure yet. Assign supervisors above.</p>
                  ) : (
                    <ul className="space-y-2">
                      {reportingTree.map((node) => (
                        <ReportingNodeItem key={node.employee.id} node={node} avatarColor={avatarColor} getInitials={getInitials} />
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
